import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { GoldenCircles, GridDots, CornerMark, VShape } from "./GeometricDecor";
import { about } from "@/lib/content";
import { getLanguageFromPath } from "@/lib/language";

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const copy = about[getLanguageFromPath(location.pathname)];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = "1";
                (el as HTMLElement).style.transform = "translateY(0)";
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-24 md:py-32 bg-white relative overflow-hidden">      
      {/* Geometric decor */}
      <GoldenCircles size={380} opacity={0.04} color="#004643" className="-top-20 -right-20" />
      <GridDots cols={6} rows={5} gap={30} opacity={0.07} color="#004643" className="left-8 top-24" />
      <VShape width={200} height={130} opacity={0.04} color="#004643" strokeWidth={1} className="bottom-20 right-16" />
      <CornerMark position="tl" size={28} color="#acd1c6" opacity={0.3} className="top-10 left-10" />
      <CornerMark position="br" size={28} color="#acd1c6" opacity={0.2} className="bottom-10 right-10" />
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Label */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 flex justify-center mb-12">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border text-xs font-medium tracking-widest uppercase"
            style={{ borderColor: "#acd1c6", color: "#004643" }}
          >
            <i className="ri-user-line text-sm" />
            {copy.label}
          </span>
        </div>

        {/* Intro */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2
            className="reveal opacity-0 translate-y-6 transition-all duration-700 text-3xl md:text-4xl lg:text-5xl font-light leading-tight mb-6"
            style={{ fontFamily: "'League Spartan', sans-serif", color: "#004643" }}
          >
            Lakatos Ivett
            <br />
            <span className="font-bold italic">Creative Designer</span>
          </h2>
          <p className="reveal opacity-0 translate-y-6 transition-all duration-700 text-gray-500 text-base leading-relaxed">
            {copy.intro}
          </p>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left: Story + Skills */}
          <div className="flex flex-col gap-10">
            {/* Story */}
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: "#004643" }}>
                <i className="ri-heart-line" style={{ color: "#acd1c6" }} />
                {copy.storyTitle}
              </h3>
              <div className="flex flex-col gap-4 text-gray-500 text-sm leading-relaxed">
                {copy.story.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </div>
            </div>

            {/* Courses */}
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: "#004643" }}>
                <i className="ri-graduation-cap-line" style={{ color: "#acd1c6" }} />
                {copy.coursesTitle}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {copy.courses.map((course) => (
                  <div
                    key={course}
                    className="flex items-start gap-2.5 rounded-xl px-4 py-3"
                    style={{ backgroundColor: "#f0f9f7" }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center rounded-full shrink-0 mt-0.5" style={{ backgroundColor: "#acd1c6" }}>
                      <i className="ri-check-line text-xs" style={{ color: "#004643" }} />
                    </div>
                    <span className="text-gray-600 text-xs leading-relaxed">{course}</span>
                  </div>
                ))}
                <div className="flex items-start gap-2.5 rounded-xl px-4 py-3 sm:col-span-2" style={{ backgroundColor: "#004643" }}>
                  <div className="w-5 h-5 flex items-center justify-center rounded-full shrink-0 mt-0.5" style={{ backgroundColor: "#acd1c6" }}>
                    <i className="ri-book-open-line text-xs" style={{ color: "#004643" }} />
                  </div>
                  <span className="text-white/80 text-xs leading-relaxed">
                    {copy.webler}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Timeline + Photo */}
          <div className="flex flex-col gap-10">
            {/* Photo */}
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 relative">
              <div className="w-full h-[320px] md:h-[400px] rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/08b8454f0c34fb7284ae1d0e5d446595.png"
                  alt="Lakatos Ivett – Creative Designer"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Profile picture - top right corner */}
              <div className="absolute -top-4 -right-4 w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-white shadow-xl overflow-hidden z-10 bg-white">
                <img
                  src="https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/adf9ee7399db5274c7e2f6772b5d40a5.png"
                  alt={copy.profileAlt}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 rounded-2xl px-5 py-3 shadow-lg" style={{ backgroundColor: "#004643" }}>
                <div className="text-xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: "#acd1c6" }}>3+</div>
                <div className="text-white/60 text-xs mt-0.5">{copy.experienceLabel}</div>
              </div>
            </div>

            {/* Timeline */}
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
              <h3 className="text-lg font-semibold mb-6 flex items-center gap-2" style={{ color: "#004643" }}>
                <i className="ri-route-line" style={{ color: "#acd1c6" }} />
                {copy.timelineTitle}
              </h3>
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-px" style={{ backgroundColor: "#acd1c6", opacity: 0.4 }} />
                <div className="flex flex-col gap-6">
                  {copy.timeline.map((item, idx) => (
                    <div key={idx} className="relative pl-12">
                      <div className="absolute left-0 top-1 w-8 h-8 flex items-center justify-center rounded-full bg-white border-2 z-10" style={{ borderColor: "#acd1c6" }}>
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#004643" }} />
                      </div>
                      <div className="text-xs font-medium tracking-wide mb-1" style={{ color: "#acd1c6" }}>{item.year}</div>
                      <div className="font-semibold text-sm mb-1" style={{ color: "#004643" }}>{item.title}</div>
                      <div className="text-gray-500 text-xs leading-relaxed">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Philosophy cards */}
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {copy.values.map((item) => (
                <div key={item.title} className="rounded-xl p-4" style={{ backgroundColor: "#f0f9f7" }}>
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg mb-3" style={{ backgroundColor: "#004643" }}>
                    <i className={`${item.icon} text-sm`} style={{ color: "#acd1c6" }} />
                  </div>
                  <div className="font-semibold text-sm mb-1" style={{ color: "#004643" }}>{item.title}</div>
                  <div className="text-gray-500 text-xs leading-relaxed">{item.desc}</div>
                </div>
              ))}
            </div>

            {/* Contact mini CTA */}
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-2xl p-5" style={{ backgroundColor: "#f0f9f7" }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ backgroundColor: "#004643" }}>
                  <i className="ri-mail-line text-sm" style={{ color: "#acd1c6" }} />
                </div>
                <div>
                  <div className="text-sm font-medium" style={{ color: "#004643" }}>workivett@gmail.com</div>
                  <div className="text-gray-400 text-xs">+36 70 285 8571</div>
                </div>
              </div>
              <button
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                className="sm:ml-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: "#004643", color: "#acd1c6" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#005a56"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#004643"; }}
              >
                {copy.cta}
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
